import { useRef, useEffect, useState, useCallback } from 'react'
import { Play, Pause, Volume2 } from 'lucide-react'

interface ShahadaAudioPlayerProps {
  audioUrl: string | null
  shahadaText?: string // Text to speak if audio file fails
  languageCode?: string // Language code for TTS fallback
  isRTL?: boolean
  compact?: boolean // Compact mode for Figma design
}

export default function ShahadaAudioPlayer({ 
  audioUrl, 
  shahadaText,
  languageCode,
  isRTL = false,
  compact = false
}: ShahadaAudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const synthRef = useRef<SpeechSynthesis | null>(null)
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [audioError, setAudioError] = useState(false)
  const [useTTS, setUseTTS] = useState(false) // Use TTS when audio file fails

  // Validate that audioUrl is valid (can be local file or remote URL)
  // NO browser speech synthesis fallback - only audio files
  const isValidAudioUrl = (url: string | null): boolean => {
    if (!url) return false
    // Accept local files (starting with /) or remote HTTP/HTTPS URLs
    if (url.startsWith('/')) return true // Local file
    try {
      const urlObj = new URL(url)
      return (urlObj.protocol === 'http:' || urlObj.protocol === 'https:')
    } catch {
      return false
    }
  }

  // Initialize audio element and speech synthesis
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio()
      audioRef.current.preload = 'metadata'
    }
    
    // Initialize Web Speech API for TTS fallback
    if ('speechSynthesis' in window) {
      synthRef.current = window.speechSynthesis
    }
    
    return () => {
      // Cleanup: stop any ongoing speech
      if (synthRef.current) {
        synthRef.current.cancel()
      }
    }
  }, [])

  // Update audio when URL changes
  useEffect(() => {
    if (audioRef.current && audioUrl && isValidAudioUrl(audioUrl)) {
      resetAudio()
      setUseTTS(false) // Try audio file first
      loadAudio(audioUrl)
    } else if (!audioUrl) {
      // No audio URL - will use TTS if text is available
      if (shahadaText) {
        setUseTTS(true)
        setAudioError(false)
      } else {
        setUseTTS(false)
        setAudioError(true)
      }
    } else if (!isValidAudioUrl(audioUrl)) {
      // Invalid URL - will use TTS if text is available
      if (shahadaText) {
        setUseTTS(true)
        setAudioError(false)
      } else {
        setUseTTS(false)
        setAudioError(true)
      }
    }
  }, [audioUrl, shahadaText])

  const loadAudio = (url: string) => {
    if (!audioRef.current) return
    
    // Ensure URL is valid (local file or remote URL)
    if (!isValidAudioUrl(url)) {
      // Invalid URL - fallback to TTS if text available
      if (shahadaText) {
        setUseTTS(true)
        setAudioError(false)
      } else {
        setAudioError(true)
      }
      return
    }
    
    // Clear any previous errors before attempting to load
    setAudioError(false)
    setUseTTS(false)
    
    // Set up error handler BEFORE loading
    const handleLoadError = () => {
      // Audio file failed - fallback to TTS if text is available
      if (shahadaText && synthRef.current) {
        setUseTTS(true)
        setAudioError(false)
      } else {
        setAudioError(true)
        setIsPlaying(false)
      }
    }
    
    // Remove any existing error listeners
    audioRef.current.removeEventListener('error', handleLoadError)
    
    // Add error handler
    audioRef.current.addEventListener('error', handleLoadError, { once: true })
    
    // Try to load the audio file
    audioRef.current.src = url
    audioRef.current.load()
  }

  // TTS function - converts text to speech as fallback
  const speakWithTTS = useCallback(() => {
    if (!synthRef.current || !shahadaText || !languageCode) {
      setAudioError(true)
      return
    }

    // Cancel any ongoing speech
    synthRef.current.cancel()

    const utterance = new SpeechSynthesisUtterance(shahadaText)
    
    // Map language codes to TTS language codes
    const langMap: Record<string, string> = {
      'ar': 'ar-SA',
      'en': 'en-US',
      'fr': 'fr-FR',
      'es': 'es-ES',
      'de': 'de-DE',
      'it': 'it-IT',
      'pt': 'pt-PT',
      'ru': 'ru-RU',
      'tr': 'tr-TR',
      'ur': 'ur-PK',
      'id': 'id-ID',
      'ms': 'ms-MY',
      'hi': 'hi-IN',
      'bn': 'bn-BD',
      'sw': 'sw-KE',
      'zh': 'zh-CN',
      'ja': 'ja-JP',
      'ko': 'ko-KR',
    }

    utterance.lang = langMap[languageCode] || languageCode
    utterance.rate = 0.8
    utterance.pitch = 1
    utterance.volume = 1

    // Get voices and select appropriate one
    const voices = synthRef.current.getVoices()
    const voice = voices.find(v => {
      const vLang = v.lang.toLowerCase()
      const uLang = utterance.lang.toLowerCase()
      return vLang.startsWith(uLang) || vLang.includes(uLang.split('-')[0])
    }) || voices[0]

    if (voice) {
      utterance.voice = voice
    }

    utterance.onstart = () => {
      setIsPlaying(true)
      setAudioError(false)
    }

    utterance.onend = () => {
      setIsPlaying(false)
      setCurrentTime(0)
    }

    utterance.onerror = (event) => {
      if (event.error !== 'interrupted' && event.error !== 'canceled') {
        setAudioError(true)
      }
      setIsPlaying(false)
    }

    utteranceRef.current = utterance
    synthRef.current.speak(utterance)
  }, [shahadaText, languageCode])

  const resetAudio = () => {
    // Stop audio file
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    // Stop TTS
    if (synthRef.current) {
      synthRef.current.cancel()
    }
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
    setAudioError(false)
    setUseTTS(false)
  }

  const toggleAudio = () => {
    if (isPlaying) {
      // Stop playing
      if (useTTS && synthRef.current) {
        synthRef.current.cancel()
      } else if (audioRef.current) {
        audioRef.current.pause()
      }
      setIsPlaying(false)
      setAudioError(false)
    } else {
      // Start playing
      // Check if we should use TTS (no audio URL, invalid URL, or audio failed)
      const shouldUseTTS = useTTS || !audioUrl || !isValidAudioUrl(audioUrl)
      
      if (shouldUseTTS) {
        // Use TTS fallback
        if (shahadaText && synthRef.current) {
          setUseTTS(true)
          speakWithTTS()
        } else {
          setAudioError(true)
        }
      } else {
        // Use audio file
        if (!audioRef.current || !audioUrl) {
          // No audio file - try TTS if available
          if (shahadaText && synthRef.current) {
            setUseTTS(true)
            speakWithTTS()
          } else {
            setAudioError(true)
          }
          return
        }
        
        setAudioError(false)
        audioRef.current.play().catch((err) => {
          // Audio play failed - try TTS fallback
          if (shahadaText && synthRef.current) {
            setUseTTS(true)
            speakWithTTS()
          } else {
            setAudioError(true)
            setIsPlaying(false)
          }
        })
      }
    }
  }

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime)
    const handleLoadedMetadata = () => setDuration(audio.duration)
    const handleEnded = () => setIsPlaying(false)
    const handleError = () => {
      setAudioError(true)
      setIsPlaying(false)
    }
    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('error', handleError)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
    }
  }, [audioUrl])

  const formatTime = (sec: number) => {
    if (!sec || Number.isNaN(sec)) return '0:00'
    const m = Math.floor(sec / 60)
    const s = Math.floor(sec % 60).toString().padStart(2, '0')
    return `${m}:${s}`
  }

  if (compact) {
    // Compact mode - Figma design (no card wrapper, just controls)
    return (
      <>
        {/* Audio Player Controls - Compact Figma Style */}
        <div className="flex items-center gap-3">
          {/* Play Button - Black/Dark */}
          <button
            onClick={toggleAudio}
            disabled={(!audioUrl && !shahadaText) || (audioError && !useTTS)}
            className="w-10 h-10 rounded-full bg-[#1A1A1A] dark:bg-gray-800 flex items-center justify-center text-white hover:bg-[#2A2A2A] dark:hover:bg-gray-700 transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
          >
            {isPlaying ? (
              <Pause className="w-5 h-5" />
            ) : (
              <Play className={`w-5 h-5 ${isRTL ? 'mr-0.5' : 'ml-0.5'}`} />
            )}
          </button>

          {/* Waveform Visualization - More bars for Figma style */}
          <div className="flex items-end gap-0.5 h-8 flex-1">
            {[2, 4, 6, 8, 10, 8, 6, 4, 3, 5, 7, 9, 7, 5, 3, 4, 6, 8, 6, 4].map((h, i) => (
              <div
                key={i}
                className={`w-0.5 rounded-sm bg-gray-800 dark:bg-gray-300 ${
                  isPlaying ? 'animate-pulse opacity-100' : 'opacity-40'
                }`}
                style={{
                  height: `${h * 2.5}px`,
                  animationDelay: `${i * 0.05}s`,
                }}
              />
            ))}
          </div>

          {/* Time Display */}
          <span className="text-gray-700 dark:text-gray-300 text-sm font-medium min-w-[45px] text-right">
            {formatTime(isPlaying ? currentTime : duration)}
          </span>

          {/* Volume Icon */}
          <Volume2 className="text-gray-500 dark:text-gray-400 w-5 h-5 flex-shrink-0" />
        </div>

        {/* Audio Error Message - Only show if TTS is not available */}
        {audioError && !useTTS && (
          <div className="mt-2 text-xs text-red-600 dark:text-red-400 text-center">
            {!audioUrl || !isValidAudioUrl(audioUrl)
              ? 'Audio not available for this language'
              : 'Unable to load audio file. Please check your connection or try again later.'}
          </div>
        )}

        {/* Hidden audio element */}
        <audio ref={audioRef} preload="metadata" />
      </>
    )
  }

  // Full card mode (original design)
  return (
    <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4 max-w-md mx-auto mb-4">
      {/* Audio Player Controls */}
      <div className={`flex items-center gap-3 ${!audioUrl ? 'pt-0' : ''}`}>
        {/* Play Button */}
        <button
          onClick={toggleAudio}
          disabled={(!audioUrl && !shahadaText) || (audioError && !useTTS)}
          className="w-10 h-10 rounded-full bg-gray-800 dark:bg-gray-700 flex items-center justify-center text-white hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
        >
          {isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className={`w-5 h-5 ${isRTL ? 'mr-0.5' : 'ml-0.5'}`} />
          )}
        </button>

        {/* Waveform Visualization */}
        <div className="flex items-end gap-1 h-8 flex-1">
          {[3, 5, 8, 6, 4, 7, 5, 3].map((h, i) => (
            <div
              key={i}
              className={`w-1 rounded bg-gray-600 dark:bg-gray-400 ${
                isPlaying ? 'animate-pulse opacity-100' : 'opacity-40'
              }`}
              style={{
                height: `${h * 3}px`,
                animationDelay: `${i * 0.1}s`,
              }}
            />
          ))}
        </div>

        {/* Time Display */}
        <span className="text-gray-700 dark:text-gray-300 text-sm font-medium min-w-[40px]">
          {formatTime(isPlaying ? currentTime : duration)}
        </span>

        {/* Volume Icon */}
        <Volume2 className="text-gray-600 dark:text-gray-400 w-5 h-5 flex-shrink-0" />
      </div>

      {/* Audio Error Message - Only show if TTS is not available */}
      {audioError && !useTTS && (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400 text-center">
          {!audioUrl || !isValidAudioUrl(audioUrl)
            ? 'Audio not available for this language'
            : 'Unable to load audio file. Please check your connection or try again later.'}
        </div>
      )}

      {/* Hidden audio element */}
      <audio ref={audioRef} preload="metadata" />
    </div>
  )
}
